import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { THEMES, DEFAULT_THEME, isThemeId, themeById, nextThemeId } from './data/themes';
import { readStored, writeStored } from './storage';

/**
 * Which of the ten visual modes is showing.
 *
 * Kept apart from SiteContext on purpose: that one owns filtering, this one owns
 * appearance, and they never talk to each other. The choice round-trips through
 * the URL the same way filters do, so `?theme=terminal` is a link you can send.
 */

const ThemeContext = createContext(null);

export const STORAGE_KEY = 'ht-theme';

export const useTheme = () => {
  const value = useContext(ThemeContext);
  if (!value) throw new Error('useTheme must be used inside <ThemeProvider>');
  return value;
};

/**
 * Pure so it can be tested without a DOM, and so the inline script in
 * public/index.html can mirror it exactly.
 *
 * KEEP IN SYNC with that script — it runs before this module loads, and a
 * disagreement shows up as a flash of the wrong theme on first paint.
 * src/prepaint.test.js fails if the two drift apart.
 */
export function resolveTheme({ search = '', stored = null } = {}) {
  const fromUrl = new URLSearchParams(search).get('theme');
  if (isThemeId(fromUrl)) return fromUrl;
  if (isThemeId(stored)) return stored;
  return DEFAULT_THEME;
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() =>
    resolveTheme({
      search: typeof window === 'undefined' ? '' : window.location.search,
      stored: readStored(STORAGE_KEY),
    })
  );

  useEffect(() => {
    const root = document.documentElement;
    const descriptor = themeById(theme);

    root.setAttribute('data-theme', theme);
    // Gets the browser's own chrome — scrollbars, form controls, the overscroll
    // ground — to match, which custom properties alone cannot do.
    root.style.colorScheme = descriptor.scheme;

    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', descriptor.swatch[0]);

    writeStored(STORAGE_KEY, theme);

    // replaceState, not pushState: switching themes is not navigation, and
    // stacking history entries would make the back button feel broken.
    const params = new URLSearchParams(window.location.search);
    if (theme === DEFAULT_THEME) params.delete('theme');
    else params.set('theme', theme);

    const search = params.toString();
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`
    );
  }, [theme]);

  const setTheme = useCallback((id) => {
    if (isThemeId(id)) setThemeState(id);
  }, []);

  const cycleTheme = useCallback(() => {
    setThemeState((current) => nextThemeId(current));
  }, []);

  const value = useMemo(
    () => ({ theme, setTheme, cycleTheme, themes: THEMES }),
    [theme, setTheme, cycleTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
